"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import CaseStudyCarousel from "@/app/(public)/shared/components/CaseStudyCarousel";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
// import { MarkdownDisplay } from "@/app/(public)/shared/ui/MarkdownDisplay";
import { fetchPublicCaseStudies } from "@/lib/publications/api";
import type { CaseStudy } from "@/lib/publications/types";
import { Briefcase, Building2, Calendar, Clock, MapPin, Users, ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { HtmlDisplay } from "@/components/ui/admin-dash/publications-editor/shared/HtmlDisplay";

// Table of Contents types
interface TOCItem {
  id: string;
  text: string;
  level: number;
}

// Function to extract table of contents from HTML content
const extractTableOfContentsFromHtml = (htmlContent: string): TOCItem[] => {
  const tocItems: TOCItem[] = [];

  // Use regex to find heading tags in HTML
  const headingRegex = /<h([1-3])[^>]*>(.*?)<\/h\1>/gi;
  let match;
  let index = 0;

  while ((match = headingRegex.exec(htmlContent)) !== null) {
    const level = parseInt(match[1]);
    // Remove HTML tags and decode HTML entities
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = match[2];
    const text = tempDiv.textContent?.trim() || "";

    if (text && level <= 3) {
      const id = `heading-${index}-${text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      tocItems.push({ id, text, level });
      index++;
    }
  }

  return tocItems;
};

export default function CaseStudyPage() {
  const params = useParams();
  const slug = params?.slug as string;

  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);
  const [allCaseStudies, setAllCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroImgSrc, setHeroImgSrc] = useState<string>("/default-profile.png");
  const [logoImgSrc, setLogoImgSrc] = useState<string>("/default-profile.png");
  const [venueImgSrc, setVenueImgSrc] = useState<string>("/default-profile.png");
  const [activeSection, setActiveSection] = useState<string>("");

  const { isModalOpen, openModal, closeModal } = useModalManager();

  useEffect(() => {
    if (!slug) return;

    const loadCaseStudy = async () => {
      try {
        setLoading(true);
        setError(null);
        const caseStudies = await fetchPublicCaseStudies();
        const foundCaseStudy = caseStudies.find((cs) => cs.slug === slug);
        setCaseStudy(foundCaseStudy || null);
        setAllCaseStudies(caseStudies);

        // Set image sources for error handling
        if (foundCaseStudy) {
          const isValidUrl = (url: string) => {
            if (!url || url === "???" || url.trim() === "") return false;
            try {
              new URL(url);
              return true;
            } catch {
              return false;
            }
          };

          setHeroImgSrc(
            isValidUrl(foundCaseStudy.heroImage.url) ? foundCaseStudy.heroImage.url : "/default-profile.png"
          );
          setLogoImgSrc(
            isValidUrl(foundCaseStudy.company.logo.url) ? foundCaseStudy.company.logo.url : "/default-profile.png"
          );
          if (foundCaseStudy.venue.images && foundCaseStudy.venue.images.length > 0) {
            setVenueImgSrc(
              isValidUrl(foundCaseStudy.venue.images[0].url)
                ? foundCaseStudy.venue.images[0].url
                : "/default-profile.png"
            );
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load case study");
        console.error("Error fetching case study:", err);
      } finally {
        setLoading(false);
      }
    };

    loadCaseStudy();
  }, [slug]);

  // Error handling functions for images
  const handleHeroImageError = () => {
    console.warn(`Failed to load hero image: ${caseStudy?.heroImage.url}`);
    setHeroImgSrc("/default-profile.png");
  };

  const handleLogoError = () => {
    console.warn(`Failed to load logo image: ${caseStudy?.company.logo.url}`);
    setLogoImgSrc("/default-profile.png");
  };

  const handleVenueImageError = () => {
    console.warn(`Failed to load venue image: ${caseStudy?.venue.images?.[0]?.url}`);
    setVenueImgSrc("/default-profile.png");
  };

  // Generate table of contents from case study content
  const tableOfContents = useMemo(() => {
    if (!caseStudy?.content) return [];
    return extractTableOfContentsFromHtml(caseStudy.content);
  }, [caseStudy?.content]);

  // Scroll to section handler - updated for HTML content with nav bar offset
  const scrollToSection = (text: string) => {
    // Find all headings in the content area and scroll to the matching one
    const contentArea = document.querySelector(".prose");
    if (contentArea) {
      const headings = contentArea.querySelectorAll("h1, h2, h3");
      const targetHeading = Array.from(headings).find((heading) => heading.textContent?.trim() === text);
      if (targetHeading) {
        // Get nav bar height dynamically based on screen size
        const isMobile = window.innerWidth < 1024; // lg breakpoint
        const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024; // md to lg

        let navBarHeight = 80; // Default desktop height
        if (isMobile) {
          navBarHeight = 56; // Mobile navbar height
        } else if (isTablet) {
          navBarHeight = 62; // Tablet navbar height
        }

        // Get the element's position relative to the viewport
        const elementRect = targetHeading.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;

        // Scroll to position with minimal padding - just navbar height + small offset
        window.scrollTo({
          top: absoluteElementTop - navBarHeight - 10, // Reduced to just 10px padding
          behavior: "smooth",
        });

        setActiveSection(text);
      }
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-landing-bg-secondary" data-section="case-studies">
        {/* Hero Section Skeleton */}
        <div className="relative h-[calc(100vh-64px)] md:h-[calc(100vh-64px)] overflow-hidden mt-[64px]">
          <div className="w-full h-full bg-gray-200 animate-pulse" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/80" />

          {/* Hero Content Skeleton */}
          <div className="absolute inset-0 flex items-end">
            <div className="container-page pb-8 md:pb-16">
              <div className="flex flex-col gap-4 md:gap-6">
                <div className="w-16 h-10 bg-white/20 rounded animate-pulse" />
                <div className="max-w-4xl space-y-4">
                  <div className="w-3/4 h-12 md:h-16 bg-white/20 rounded animate-pulse" />
                  <div className="w-full h-5 md:h-6 bg-white/20 rounded animate-pulse" />
                  <div className="w-2/3 h-5 md:h-6 bg-white/20 rounded animate-pulse" />
                </div>
                <div className="flex flex-wrap items-center gap-6 md:gap-12 lg:gap-24">
                  <div className="text-center space-y-2">
                    <div className="w-16 h-6 md:h-8 bg-white/20 rounded animate-pulse" />
                    <div className="w-12 h-3 md:h-4 bg-white/20 rounded animate-pulse" />
                  </div>
                  <div className="text-center space-y-2">
                    <div className="w-16 h-6 md:h-8 bg-white/20 rounded animate-pulse" />
                    <div className="w-12 h-3 md:h-4 bg-white/20 rounded animate-pulse" />
                  </div>
                  <div className="text-center space-y-2">
                    <div className="w-16 h-6 md:h-8 bg-white/20 rounded animate-pulse" />
                    <div className="w-12 h-3 md:h-4 bg-white/20 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial Section Skeleton */}
        <div className="w-full flex justify-center items-center py-8 md:py-16">
          <div className="container-page">
            <div className="text-center py-8 md:py-12 space-y-4 md:space-y-6">
              <div className="w-3/4 h-6 md:h-8 bg-gray-200 rounded animate-pulse mx-auto" />
              <div className="w-2/3 h-6 md:h-8 bg-gray-200 rounded animate-pulse mx-auto" />
              <div className="flex items-center justify-center gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 rounded-xl animate-pulse" />
                <div className="space-y-2">
                  <div className="w-24 md:w-32 h-4 md:h-5 bg-gray-200 rounded animate-pulse" />
                  <div className="w-20 md:w-24 h-3 md:h-4 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section Skeleton */}
        <div className="w-full flex justify-center items-center py-12 md:py-24">
          <div className="container-page flex flex-col gap-8 md:gap-16">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-12">
              {/* Left Column Skeleton */}
              <div className="max-w-3xl space-y-6 md:space-y-8">
                <div className="aspect-video bg-gray-200 rounded-2xl animate-pulse" />
                <div className="space-y-3 md:space-y-4">
                  <div className="w-full h-5 md:h-6 bg-gray-200 rounded animate-pulse" />
                  <div className="w-full h-5 md:h-6 bg-gray-200 rounded animate-pulse" />
                  <div className="w-3/4 h-5 md:h-6 bg-gray-200 rounded animate-pulse" />
                  <div className="w-full h-24 md:h-32 bg-gray-200 rounded animate-pulse" />
                  <div className="w-full h-5 md:h-6 bg-gray-200 rounded animate-pulse" />
                  <div className="w-4/5 h-5 md:h-6 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>

              {/* Right Sidebar Skeleton */}
              <div className="w-full lg:w-80 space-y-6 md:space-y-8">
                <div className="bg-white p-4 md:p-6 rounded-2xl border border-gray-100">
                  <div className="space-y-3 md:space-y-4">
                    <div className="w-32 h-5 md:h-6 bg-gray-200 rounded animate-pulse" />
                    <div className="space-y-2 md:space-y-3">
                      <div className="flex justify-between">
                        <div className="w-20 h-3 md:h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="w-16 h-3 md:h-4 bg-gray-200 rounded animate-pulse" />
                      </div>
                      <div className="flex justify-between">
                        <div className="w-24 h-3 md:h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="w-12 h-3 md:h-4 bg-gray-200 rounded animate-pulse" />
                      </div>
                      <div className="flex justify-between">
                        <div className="w-16 h-3 md:h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="w-20 h-3 md:h-4 bg-gray-200 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-landing-bg-secondary">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <div className="text-h2 landing-text font-medium mb-2 text-landing-primary">Error Loading Case Study</div>
          <div className="text-body landing-text mb-6 text-landing-secondary">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mr-4"
          >
            Retry
          </button>
          <Link href="/case-studies">
            <Button variant="primary" color="primary" size="md">
              Back to Case Studies
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-landing-bg-secondary">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <div className="text-h2 landing-text font-medium mb-2 text-landing-primary">Case Study Not Found</div>
          <div className="text-body landing-text mb-6 text-landing-secondary">
            The case study you're looking for doesn't exist.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-landing-bg-secondary" data-section="case-studies">
      {/* Hero Section */}
      <div className="relative h-[calc(100vh-64px)] md:h-[calc(100vh-64px)] overflow-hidden mt-[64px]">
        {/* Back Arrow */}
        <Link
          href="/case-studies"
          className="absolute top-6 left-6 z-20 flex items-center gap-2 bg-white/80 hover:bg-white transition rounded-full px-3 py-2 shadow-md"
        >
          <ArrowLeft className="w-5 h-5 text-landing-primary" />
          <span className="hidden md:inline text-landing-primary font-medium">Back</span>
        </Link>
        <img
          src={heroImgSrc}
          alt={caseStudy.heroImage.alt}
          className="w-full h-full object-cover"
          onError={handleHeroImageError}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/80" />

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-end">
          <div className="container-page pb-8 md:pb-16">
            <div className="flex flex-col gap-4 md:gap-6 text-white">
              {/* Company Logo */}
              <div className="flex justify-start">
                <img
                  className="w-auto h-8 md:h-10 object-contain filter invert brightness-0 max-w-[120px] md:max-w-[160px]"
                  src={logoImgSrc}
                  alt={caseStudy.company.logo.alt}
                  onError={handleLogoError}
                />
              </div>

              {/* Title */}
              <div className="max-w-4xl">
                <h1 className="text-h1 text-white mb-4 md:mb-6">{caseStudy.title}</h1>
                <p className="text-body-lg text-white/70 leading-relaxed max-w-3xl">{caseStudy.description}</p>
              </div>

              {/* Key Metrics in Hero */}
              <div className="flex flex-wrap items-center gap-6 md:gap-12 lg:gap-24">
                {caseStudy.metrics.map((metric, index) => (
                  <div key={index} className="text-center">
                    <div className="text-metric-lg mb-1 md:mb-2 text-white">{metric.value}</div>
                    <div className="text-body-sm font-medium text-white/70">{metric.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Section - Below Hero */}
      <div className="w-full flex justify-center items-center py-8 md:py-16">
        <div className="container-page">
          <div className="text-center py-8 md:py-12">
            <div className="text-4xl md:text-6xl mb-4 md:mb-6 opacity-20">"</div>
            <blockquote className="text-lg md:text-2xl font-medium leading-relaxed mb-6 md:mb-8 text-landing-primary">
              {caseStudy.testimonial.quote}
            </blockquote>
            <div className="flex items-center justify-center gap-3 md:gap-4">
              <img
                src={logoImgSrc}
                alt={caseStudy.company.logo.alt}
                className="w-10 h-10 md:w-12 md:h-12 object-contain rounded-xl p-2"
                onError={handleLogoError}
              />
              <div className="text-left">
                <div className="text-body-lg md:text-lg font-bold text-landing-primary">
                  {caseStudy.testimonial.author.name}
                </div>
                <div className="text-body md:text-base text-landing-secondary">
                  {caseStudy.testimonial.author.title}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="w-full flex justify-center items-center py-12 md:py-24">
        <div className="container-page flex flex-col gap-8 md:gap-16">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12">
            {/* Left Sidebar - Table of Contents */}
            <div className="w-64">
              <div className="sticky top-24">
                {/* Table of Contents */}
                {tableOfContents.length > 0 && (
                  <div className="space-y-4">
                    <div>
                      <div className="text-body font-semibold mb-4 pb-2 border-b text-landing-primary border-landing-border-primary">
                        In this case study
                      </div>
                      <nav className="space-y-3">
                        {tableOfContents.map((item, index) => (
                          <button
                            key={`${item.text}-${index}`}
                            onClick={() => scrollToSection(item.text)}
                            className={`block w-full text-left text-body-sm landing-text hover:opacity-100 transition-all duration-200 ${
                              activeSection === item.text ? "opacity-100" : "opacity-70"
                            }`}
                          >
                            {item.text}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Main Content - Constrained width */}
            <div className="max-w-3xl">
              {/* Venue Image */}
              {caseStudy.venue.images && caseStudy.venue.images.length > 0 && (
                <div className="mb-6 md:mb-8">
                  <img
                    src={venueImgSrc}
                    alt={caseStudy.venue.images[0].alt}
                    className="w-full aspect-video object-cover rounded-2xl"
                    onError={handleVenueImageError}
                  />
                  <div className="mt-2 md:mt-3 text-center">
                    <div className="text-body-sm text-landing-secondary">
                      {caseStudy.venue.name}, {caseStudy.venue.location}
                    </div>
                  </div>
                </div>
              )}

              {/* HTML Content */}
              <HtmlDisplay
                content={
                  caseStudy.content ||
                  `# ${caseStudy.title}

${caseStudy.description}

## The Challenge

This case study demonstrates how ${caseStudy.company.name} successfully planned their ${caseStudy.event.type.toLowerCase()} using Nowadays.

## The Solution

By leveraging Nowadays' AI-powered platform, ${caseStudy.company.name} was able to:

- Save significant time in the planning process
- Reduce costs through optimized venue selection
- Create an exceptional experience for their team

## The Results

The event was a resounding success, achieving ${caseStudy.metrics.map((m) => `${m.value} ${m.label ? m.label.toLowerCase() : "results"}`).join(", ")} and high team satisfaction.`
                }
              />
            </div>
          </div>
        </div>
      </div>
      {/* Continue Exploring Section */}
      <div className="py-8 md:py-12">
        <CaseStudyCarousel
          variant="continue-exploring"
          caseStudies={allCaseStudies}
          excludeCreatedAt={caseStudy.createdAt}
          className="container-page"
        />
      </div>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Book a Demo"
        subtitle="Schedule a 30-minute demo with our team"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your demo with our team"
      />
    </div>
  );
}
